import { useCallback, useRef, useEffect } from 'react';
import { getWorkerManager, areWorkersSupported } from './worker-manager';

function processChunkSync(
  chunk: string,
  isLastChunk: boolean,
  existingPartial: string,
): { lines: string[]; partial: string } {
  const combined = existingPartial + chunk;
  const lines = combined.split('\n');

  if (isLastChunk) {
    return { lines, partial: '' };
  } else {
    const partial = lines.pop() || '';
    return { lines, partial };
  }
}

function splitLinesSync(
  text: string,
  startLine: number,
  lineCount: number,
): { lines: string[]; hasMore: boolean } {
  const allLines = text.split('\n');
  const endLine = Math.min(startLine + lineCount, allLines.length);
  const lines = allLines.slice(startLine, endLine);
  const hasMore = endLine < allLines.length;

  return { lines, hasMore };
}

function countLinesSync(text: string): number {
  if (!text) return 0;

  let count = 1;
  let pos = 0;

  while ((pos = text.indexOf('\n', pos)) !== -1) {
    count++;
    pos++;
  }

  return count;
}

const WORKER_THRESHOLD = 50000;

export type ProcessChunkInput = {
  chunk: string;
  isLastChunk: boolean;
  existingPartial: string;
};

export type ProcessChunkResult = {
  lines: string[];
  partial: string;
};

export type SplitLinesInput = {
  text: string;
  startLine: number;
  lineCount: number;
};

export type SplitLinesResult = {
  lines: string[];
  hasMore: boolean;
};

export function useBodyProcessor() {
  const useWorkerRef = useRef(areWorkersSupported());
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const processChunk = useCallback(
    async (input: ProcessChunkInput): Promise<ProcessChunkResult> => {
      const totalSize = input.chunk.length + input.existingPartial.length;

      if (!useWorkerRef.current || totalSize < WORKER_THRESHOLD) {
        return processChunkSync(
          input.chunk,
          input.isLastChunk,
          input.existingPartial,
        );
      }

      const manager = getWorkerManager();
      if (!manager) {
        return processChunkSync(
          input.chunk,
          input.isLastChunk,
          input.existingPartial,
        );
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<
          ProcessChunkInput,
          ProcessChunkResult
        >('body-processor', 'processChunk', input);
        if (signal.aborted) return { lines: [], partial: '' };
        return result;
      } catch (error) {
        if (signal.aborted) return { lines: [], partial: '' };
        console.warn('Worker processChunk failed, using sync fallback:', error);
        return processChunkSync(
          input.chunk,
          input.isLastChunk,
          input.existingPartial,
        );
      }
    },
    [],
  );

  const splitLines = useCallback(
    async (input: SplitLinesInput): Promise<SplitLinesResult> => {
      if (!useWorkerRef.current || input.text.length < WORKER_THRESHOLD) {
        return splitLinesSync(input.text, input.startLine, input.lineCount);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return splitLinesSync(input.text, input.startLine, input.lineCount);
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<
          SplitLinesInput,
          SplitLinesResult
        >('body-processor', 'splitLines', input);
        if (signal.aborted) return { lines: [], hasMore: false };
        return result;
      } catch (error) {
        if (signal.aborted) return { lines: [], hasMore: false };
        console.warn('Worker splitLines failed, using sync fallback:', error);
        return splitLinesSync(input.text, input.startLine, input.lineCount);
      }
    },
    [],
  );

  const countLines = useCallback(async (text: string): Promise<number> => {
    if (!useWorkerRef.current || text.length < WORKER_THRESHOLD) {
      return countLinesSync(text);
    }

    const manager = getWorkerManager();
    if (!manager) {
      return countLinesSync(text);
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const result = await manager.sendMessage<
        { text: string },
        { lineCount: number }
      >('body-processor', 'countLines', { text });
      if (signal.aborted) return 0;
      return result.lineCount;
    } catch (error) {
      if (signal.aborted) return 0;
      console.warn('Worker countLines failed, using sync fallback:', error);
      return countLinesSync(text);
    }
  }, []);

  return {
    processChunk,
    splitLines,
    countLines,
    processChunkSync,
    splitLinesSync,
    countLinesSync,
    isWorkerSupported: useWorkerRef.current,
    workerThreshold: WORKER_THRESHOLD,
  };
}

export { processChunkSync, splitLinesSync, countLinesSync };
